// Port of BivouacEngine/SuitabilityEvaluator.swift (PhysicalAssessment, WeatherAssessment, SuitabilityEvaluator).

import type {
  BivouacReason,
  BivouacReasonKind,
  EpochMs,
  LandCover,
  ReasonCategory,
  ReasonImpact,
  Sensitivity,
  TerrainHazard,
  TerrainSample,
  ThunderstormRisk,
  WeatherSnapshot,
  WindExposure,
} from '../core/models';
import { compareThunderstormRisk } from '../core/domain';
import { BivouacScoringConfiguration } from './bivouacScoringConfiguration';

/** Physical (terrain, access, comfort) assessment of a location — independent of regulation. */
export interface PhysicalAssessment {
  /** 0…1 weighted physical score (see `BivouacScoringConfiguration`). 0 and meaningless without terrain data. */
  score: number;
  /** A hard veto applies (unsafe or unsuitable terrain); the location must never be recommended. */
  hasHardHazard: boolean;
  /** Whether a terrain sample was available. */
  hasTerrainData: boolean;
  /** One reason per evaluated component, in display order. */
  reasons: BivouacReason[];
  /** Environmental sensitivity from terrain alone (`unknown` without terrain). */
  sensitivity: Sensitivity;
}

/** Weather comfort/safety assessment of a forecast snapshot for a night outside. */
export interface WeatherAssessment {
  /** 0…1, limited by the worst factor (thunderstorm, wind, precipitation, cold). < 0.5 → caution. */
  score: number;
  /** Reasons for every notable factor. */
  reasons: BivouacReason[];
}

/** Input of `SuitabilityEvaluator.physical` (the Swift labelled parameters). */
export interface PhysicalInput {
  /** Terrain cell at the location; `undefined` → `hasTerrainData == false`, sensitivity `unknown`. */
  sample: TerrainSample | undefined;
  /** Straight-line distance to the route (m), if known. */
  distanceFromTrail: number | undefined;
  /** Detour limit; beyond it the reason `beyondMaxDetour` is emitted. */
  maxDetourMeters: number;
  /** Distance (m) to the nearest known water source (POI), if any. */
  nearbyWaterDistance: number | undefined;
  /** Nearest official overnight facility (informational, does not change the score). */
  nearbyAccommodation: { name: string; distance: number } | undefined;
  /** Night being assessed (avalanche terrain is a hard veto in `avalancheSeasonMonths`). */
  date: EpochMs;
}

/** `TerrainHazard.allCases`, in Swift declaration order (the order reasons are emitted in). */
const allTerrainHazards: readonly TerrainHazard[] = [
  'avalanche',
  'rockfall',
  'flooding',
  'glacier',
  'cliffEdge',
  'debrisFlow',
  'lightningExposure',
];

const reason = (kind: BivouacReasonKind, impact: ReasonImpact, category: ReasonCategory): BivouacReason => ({
  kind,
  impact,
  category,
});

/** Scores terrain samples and forecast snapshots. Pure and deterministic. */
export class SuitabilityEvaluator {
  /** Thresholds and weights in use. */
  readonly configuration: Readonly<BivouacScoringConfiguration>;

  constructor(configuration: Readonly<BivouacScoringConfiguration> = BivouacScoringConfiguration.default) {
    this.configuration = configuration;
  }

  // MARK: Physical

  /** Physical assessment of a terrain sample. */
  physical(input: PhysicalInput): PhysicalAssessment {
    const config = this.configuration;
    const trailDistance = SuitabilityEvaluator.finite(input.distanceFromTrail);
    const waterDistance = SuitabilityEvaluator.finite(input.nearbyWaterDistance);
    const sample = input.sample;

    if (!sample || !Number.isFinite(sample.slopeDegrees) || !Number.isFinite(sample.elevation)) {
      const reasons = [reason({ kind: 'insufficientTerrainData' }, 'caution', 'dataQuality')];
      this.trailComponent(trailDistance, input.maxDetourMeters, reasons);
      if (waterDistance !== undefined && waterDistance <= config.waterSourceFarRadiusMeters) {
        reasons.push(
          reason(
            { kind: 'waterNearby', meters: waterDistance },
            waterDistance <= config.waterSourceRadiusMeters ? 'positive' : 'neutral',
            'access',
          ),
        );
      }
      SuitabilityEvaluator.appendAccommodation(input.nearbyAccommodation, reasons);
      return { score: 0, hasHardHazard: false, hasTerrainData: false, reasons, sensitivity: 'unknown' };
    }

    const reasons: BivouacReason[] = [];

    // Slope.
    const slope = Math.abs(sample.slopeDegrees);
    let slopeScore: number;
    if (slope <= config.idealSlopeDegrees) {
      slopeScore = 1;
      reasons.push(reason({ kind: 'slope', degrees: slope }, 'positive', 'terrain'));
    } else if (slope <= config.acceptableSlopeDegrees) {
      slopeScore =
        1 - 0.3 * SuitabilityEvaluator.fraction(slope, config.idealSlopeDegrees, config.acceptableSlopeDegrees);
      reasons.push(reason({ kind: 'slope', degrees: slope }, 'neutral', 'terrain'));
    } else if (slope <= config.maxSlopeDegrees) {
      slopeScore =
        0.7 - 0.5 * SuitabilityEvaluator.fraction(slope, config.acceptableSlopeDegrees, config.maxSlopeDegrees);
      reasons.push(reason({ kind: 'slope', degrees: slope }, 'negative', 'terrain'));
    } else {
      slopeScore = 0;
      reasons.push(reason({ kind: 'slope', degrees: slope }, 'blocking', 'terrain'));
    }

    // Land cover.
    const cover = SuitabilityEvaluator.landCoverScore(sample.landCover);
    reasons.push(
      reason(
        { kind: 'landCover', landCover: sample.landCover },
        cover.impact,
        sample.landCover === 'wetland' ? 'environment' : 'terrain',
      ),
    );

    // Exposure / elevation.
    let exposureScore: number;
    switch (sample.windExposure) {
      case 'sheltered':
        exposureScore = 1;
        reasons.push(
          reason(
            {
              kind: 'windExposure',
              exposure: 'sheltered',
              shelteredFromDegrees: SuitabilityEvaluator.finite(sample.shelteredFromDegrees),
            },
            'positive',
            'terrain',
          ),
        );
        break;
      case 'moderate':
        exposureScore = 0.7;
        reasons.push(
          reason(
            {
              kind: 'windExposure',
              exposure: 'moderate',
              shelteredFromDegrees: SuitabilityEvaluator.finite(sample.shelteredFromDegrees),
            },
            'neutral',
            'terrain',
          ),
        );
        break;
      case 'exposed':
        exposureScore = 0.35;
        reasons.push(reason({ kind: 'windExposure', exposure: 'exposed' }, 'negative', 'terrain'));
        break;
      case 'unknown':
        exposureScore = 0.6;
        break;
    }
    const elevation = sample.elevation;
    let elevationScore: number;
    const lowerElevation = config.cautionElevationMeters - 600;
    if (elevation > config.cautionElevationMeters) {
      elevationScore = 0.4;
      reasons.push(reason({ kind: 'elevation', meters: elevation }, 'caution', 'terrain'));
    } else {
      elevationScore =
        elevation <= lowerElevation
          ? 1
          : 1 - 0.3 * SuitabilityEvaluator.fraction(elevation, lowerElevation, config.cautionElevationMeters);
      reasons.push(reason({ kind: 'elevation', meters: elevation }, 'neutral', 'terrain'));
    }
    const exposureComponent = 0.6 * exposureScore + 0.4 * elevationScore;

    // Discretion: trail, settlement, road.
    const trailScore = this.trailComponent(trailDistance, input.maxDetourMeters, reasons);
    let settlementScore = 0.8;
    const settlement = SuitabilityEvaluator.finite(sample.distanceToSettlement);
    if (settlement !== undefined) {
      if (settlement < config.settlementMinimumMeters) {
        settlementScore = 0.2;
        reasons.push(reason({ kind: 'distanceFromSettlement', meters: settlement }, 'negative', 'access'));
      } else {
        settlementScore = 1;
      }
    }
    let roadScore = 0.8;
    const road = SuitabilityEvaluator.finite(sample.distanceToRoad);
    if (road !== undefined) {
      if (road < config.roadMinimumMeters) {
        roadScore = 0.3;
        reasons.push(reason({ kind: 'distanceFromRoad', meters: road }, 'negative', 'access'));
      } else {
        roadScore = 1;
      }
    }
    const discretionComponent = 0.5 * trailScore + 0.25 * settlementScore + 0.25 * roadScore;

    // Water proximity: usable source nearby vs. waterway too close.
    const waterway = SuitabilityEvaluator.finite(sample.distanceToWaterway);
    let sourceDistance = waterDistance;
    if (waterway !== undefined && waterway >= config.waterwaySensitiveMeters) {
      sourceDistance = Math.min(sourceDistance ?? waterway, waterway);
    }
    let waterScore: number;
    if (sourceDistance !== undefined && sourceDistance <= config.waterSourceRadiusMeters) {
      waterScore = 1;
      reasons.push(reason({ kind: 'waterNearby', meters: sourceDistance }, 'positive', 'access'));
    } else if (sourceDistance !== undefined && sourceDistance <= config.waterSourceFarRadiusMeters) {
      waterScore = 0.6;
      reasons.push(reason({ kind: 'waterNearby', meters: sourceDistance }, 'neutral', 'access'));
    } else {
      waterScore = 0.3;
      reasons.push(reason({ kind: 'noWaterNearby' }, 'neutral', 'access'));
    }
    if (waterway !== undefined && waterway < config.waterwaySensitiveMeters) {
      if (waterway < config.waterwayMinimumMeters) {
        waterScore = 0;
        reasons.push(reason({ kind: 'distanceFromWaterway', meters: waterway }, 'blocking', 'environment'));
      } else {
        waterScore = Math.min(waterScore, 0.1);
        reasons.push(reason({ kind: 'distanceFromWaterway', meters: waterway }, 'negative', 'environment'));
      }
    }

    // Hazards.
    const avalancheSeason = BivouacScoringConfiguration.isAvalancheSeason(config, input.date);
    for (const hazard of allTerrainHazards) {
      if (!sample.hazards.includes(hazard)) continue;
      switch (hazard) {
        case 'rockfall':
        case 'debrisFlow':
        case 'cliffEdge':
        case 'flooding':
        case 'glacier':
          reasons.push(reason({ kind: 'hazard', hazard }, 'blocking', 'terrain'));
          break;
        case 'avalanche':
          reasons.push(reason({ kind: 'hazard', hazard }, avalancheSeason ? 'blocking' : 'caution', 'terrain'));
          break;
        case 'lightningExposure':
          reasons.push(reason({ kind: 'hazard', hazard }, 'caution', 'terrain'));
          break;
      }
    }

    SuitabilityEvaluator.appendAccommodation(input.nearbyAccommodation, reasons);

    // Weighted mean, then caps: any caution/negative factor keeps the score out of the green band, any blocking factor
    // is a hard veto.
    const hard = reasons.some((r) => r.impact === 'blocking');
    const caution = reasons.some((r) => r.impact === 'caution' || r.impact === 'negative');
    const w = config.weights;
    const weightSum = w.slope + w.landCover + w.waterProximity + w.discretion + w.exposure;
    let score =
      weightSum > 0
        ? (w.slope * slopeScore +
            w.landCover * cover.score +
            w.waterProximity * waterScore +
            w.discretion * discretionComponent +
            w.exposure * exposureComponent) /
          weightSum
        : 0;
    if (caution) score = Math.min(score, config.cautionScoreCap);
    if (hard) score = Math.min(score, config.hardVetoScoreCap);
    score = Math.min(1, Math.max(0, score));

    return { score, hasHardHazard: hard, hasTerrainData: true, reasons, sensitivity: this.sensitivity(sample) };
  }

  /**
   * Environmental sensitivity of a terrain sample: high — wetland / open water, waterway closer than
   * `waterwaySensitiveMeters`, alpine meadow or pasture above `highAlpineMeadowElevationMeters`; moderate — pasture,
   * forest, open forest, cultivated land; low otherwise; unknown without terrain. (Protected areas raise it to high;
   * that is applied where the regulatory assessment is known.)
   */
  sensitivity(sample: TerrainSample | undefined): Sensitivity {
    if (!sample) return 'unknown';
    const config = this.configuration;
    if (sample.landCover === 'wetland' || sample.landCover === 'water') return 'high';
    const waterway = SuitabilityEvaluator.finite(sample.distanceToWaterway);
    if (waterway !== undefined && waterway < config.waterwaySensitiveMeters) return 'high';
    if (
      (sample.landCover === 'meadow' || sample.landCover === 'pasture') &&
      Number.isFinite(sample.elevation) &&
      sample.elevation > config.highAlpineMeadowElevationMeters
    ) {
      return 'high';
    }
    switch (sample.landCover) {
      case 'pasture':
      case 'forest':
      case 'openForest':
      case 'cultivated':
        return 'moderate';
      default:
        return 'low';
    }
  }

  // MARK: Weather

  /**
   * Weather assessment of a forecast snapshot for a night outside; `undefined` if no snapshot. Wind gusts (or, without
   * gusts, the mean wind × `strongWindGustKmh / strongMeanWindKmh`) are scaled by exposure (sheltered 0.65, moderate
   * 0.85, exposed/unknown 1.0).
   */
  weather(snapshot: WeatherSnapshot | undefined, exposure: WindExposure): WeatherAssessment | undefined {
    if (!snapshot) return undefined;
    const config = this.configuration;
    const reasons: BivouacReason[] = [];
    const components: number[] = [1];

    // Thunderstorm.
    let risk: ThunderstormRisk = snapshot.thunderstormRisk;
    if (snapshot.condition === 'thunderstorm' && compareThunderstormRisk(risk, 'moderate') < 0) risk = 'moderate';
    switch (risk) {
      case 'none':
        break;
      case 'unknown':
        components.push(0.85);
        break;
      case 'low':
        components.push(0.75);
        reasons.push(reason({ kind: 'forecastThunderstorm', risk: 'low' }, 'caution', 'weather'));
        break;
      case 'moderate':
        components.push(0.4);
        reasons.push(reason({ kind: 'forecastThunderstorm', risk: 'moderate' }, 'negative', 'weather'));
        break;
      case 'high':
        components.push(0.1);
        reasons.push(reason({ kind: 'forecastThunderstorm', risk: 'high' }, 'negative', 'weather'));
        break;
    }

    // Wind: gusts, or without gusts the mean wind scaled to a gust equivalent (strongWindGustKmh / strongMeanWindKmh,
    // ≈ 1.5 in the mountains) so the same bands apply; a mean wind is never reported as gusts.
    const gustValue = SuitabilityEvaluator.finite(snapshot.windGust);
    const meanValue = SuitabilityEvaluator.finite(snapshot.windSpeed);
    const wind = gustValue ?? meanValue;
    if (wind !== undefined) {
      const factor = exposure === 'sheltered' ? 0.65 : exposure === 'moderate' ? 0.85 : 1.0;
      const strong = config.strongWindGustKmh;
      const gustEquivalent = gustValue ?? (wind * strong) / Math.max(1, config.strongMeanWindKmh);
      const effective = gustEquivalent * factor;
      let windScore: number;
      if (effective < strong / 2) windScore = 1;
      else if (effective < strong * 0.75) windScore = 0.85;
      else if (effective < strong) windScore = 0.6;
      else if (effective < (strong * 4) / 3) windScore = 0.35;
      else windScore = 0.1;
      components.push(windScore);
      if (gustEquivalent >= (strong * 2) / 3) {
        const kind: BivouacReasonKind =
          gustValue === undefined ? { kind: 'forecastMeanWind', kmh: wind } : { kind: 'forecastWind', gustKmh: wind };
        reasons.push(reason(kind, windScore < 0.5 ? 'negative' : 'caution', 'weather'));
      }
    }

    // Precipitation (probability 0…1, amount mm).
    const rawProbability = SuitabilityEvaluator.finite(snapshot.precipitationProbability);
    const probability = rawProbability === undefined ? undefined : Math.min(1, Math.max(0, rawProbability));
    const rawAmount = SuitabilityEvaluator.finite(snapshot.precipitationAmount);
    const amount = rawAmount === undefined ? undefined : Math.max(0, rawAmount);
    const p = probability ?? 0;
    const mm = amount ?? 0;
    let precipitationScore: number;
    if (mm >= 10) precipitationScore = 0.35;
    else if (p >= 0.7 || mm >= 5) precipitationScore = 0.55;
    else if (p >= 0.4 || mm >= 1) precipitationScore = 0.8;
    else precipitationScore = 1;
    // Missing precipitation data is never scored as "dry": same small penalty as an unknown thunderstorm risk.
    const noPrecipitationData = probability === undefined && amount === undefined;
    components.push(noPrecipitationData ? Math.min(precipitationScore, 0.85) : precipitationScore);
    if (precipitationScore < 1) {
      // Without a probability (MET Norway outside its Nordic area) the reason states the amount only.
      const kind: BivouacReasonKind =
        probability === undefined
          ? { kind: 'forecastPrecipitationAmount', millimeters: mm }
          : { kind: 'forecastPrecipitation', probability: p, millimeters: mm };
      reasons.push(reason(kind, precipitationScore < 0.5 ? 'negative' : 'caution', 'weather'));
    }

    // Cold.
    const temperature = SuitabilityEvaluator.finite(snapshot.feelsLike) ?? snapshot.temperature;
    if (Number.isFinite(temperature)) {
      let coldScore: number;
      if (temperature >= 5) coldScore = 1;
      else if (temperature >= 0) coldScore = 0.8;
      else if (temperature >= -5) coldScore = 0.55;
      else coldScore = 0.3;
      components.push(coldScore);
      if (temperature < 3) {
        const impact = coldScore < 0.5 ? 'negative' : 'caution';
        reasons.push(reason({ kind: 'forecastCold', celsius: temperature }, impact, 'weather'));
      }
    }

    return { score: Math.min(...components), reasons };
  }

  // MARK: Components

  /** Trail distance component (0…1); appends its reason to `reasons` (Swift `inout`). */
  trailComponent(distance: number | undefined, maxDetourMeters: number, reasons: BivouacReason[]): number {
    const config = this.configuration;
    if (distance === undefined) return 0.7;
    if (Number.isFinite(maxDetourMeters) && distance > maxDetourMeters) {
      reasons.push(reason({ kind: 'beyondMaxDetour', meters: distance }, 'negative', 'access'));
      return 0.3;
    }
    let score: number;
    let impact: ReasonImpact;
    if (distance < config.trailTooCloseMeters) {
      score = 0.2;
      impact = 'negative';
    } else if (distance < config.preferredMinimumTrailDistanceMeters) {
      score = 0.6;
      impact = 'caution';
    } else if (distance <= config.preferredMaximumTrailDistanceMeters) {
      score = 1;
      impact = 'positive';
    } else {
      score = 0.75;
      impact = 'neutral';
    }
    reasons.push(reason({ kind: 'distanceFromTrail', meters: distance }, impact, 'access'));
    return score;
  }

  /**
   * Land-cover score and reason impact; `blocking` (hard veto) for glacier / water / wetland / settlement /
   * cultivated.
   */
  static landCoverScore(cover: LandCover): { score: number; impact: ReasonImpact } {
    switch (cover) {
      case 'meadow':
        return { score: 1, impact: 'positive' };
      case 'pasture':
      case 'openForest':
        return { score: 0.8, impact: 'neutral' };
      case 'forest':
        return { score: 0.65, impact: 'neutral' };
      case 'unknown':
        return { score: 0.5, impact: 'caution' };
      case 'shrub':
        return { score: 0.45, impact: 'caution' };
      case 'rock':
        return { score: 0.4, impact: 'negative' };
      case 'scree':
        return { score: 0.3, impact: 'negative' };
      case 'glacier':
      case 'water':
      case 'wetland':
      case 'settlement':
      case 'cultivated':
        return { score: 0, impact: 'blocking' };
    }
  }

  private static appendAccommodation(
    accommodation: { name: string; distance: number } | undefined,
    reasons: BivouacReason[],
  ): void {
    if (!accommodation || !Number.isFinite(accommodation.distance)) return;
    reasons.push(
      reason(
        { kind: 'officialAccommodationNearby', name: accommodation.name, meters: accommodation.distance },
        'neutral',
        'access',
      ),
    );
  }

  /** Position of `value` in `lower…upper` as 0…1. */
  static fraction(value: number, lower: number, upper: number): number {
    if (!(upper > lower)) return 1;
    return Math.min(1, Math.max(0, (value - lower) / (upper - lower)));
  }

  static finite(value: number | undefined): number | undefined {
    return value !== undefined && Number.isFinite(value) ? value : undefined;
  }
}
